import { RemoteConfigKey } from '../../firebase/remoteConfig/keys';
import { useStoreState } from '../../models/hooks';
import { EmployeePermissionsEnum } from '../../modules/administration/team/teamPermission/teamPermission.constant';
import { PermissionCheckType } from './permission.interface';

// Overload 1: With defaultPermission — `checkPermission` accepts optional param
export function useUserPermission(options: {
  permissions: EmployeePermissionsEnum[];
  checkType?: PermissionCheckType;
}): {
  checkPermission: (options?: { permissions?: EmployeePermissionsEnum[]; checkType?: PermissionCheckType }) => boolean;
  permissions: EmployeePermissionsEnum[];
};

// Overload 2: Without defaultPermission — `checkPermission` param is optional but throws if omitted
export function useUserPermission(options?: {
  permissions?: undefined;
  checkType?: PermissionCheckType;
}): {
  checkPermission: (options?: { permissions?: EmployeePermissionsEnum[]; checkType?: PermissionCheckType }) => boolean;
  permissions: EmployeePermissionsEnum[];
};

/**
 * A hook that provides a method to check if the current user has a set of permissions.
 *
 * @param options - An object with the following properties:
 *   - permissions: An array of EmployeePermissionsEnum values to check against.
 *   - checkType: How to check the permissions. If "all", then the user must have all the permissions.
 *     If not specified, then the user must have at least one of the permissions.
 * @returns An object with two properties:
 *   - checkPermission: A function that takes an object with the same shape as the options parameter.
 *     It returns a boolean indicating whether the user has the required permissions.
 *   - permissions: An array of the permissions that the user has.
 */
export function useUserPermission(options?: { permissions?: EmployeePermissionsEnum[]; checkType?: PermissionCheckType }) {
  const userPermissions = useStoreState((s) => s.app.user?.permissions) || [];
  const isRecruiterPermissionRemoteConfigEnabled = useStoreState(
    (state) => state.remoteConfig[RemoteConfigKey.ENABLE_RECRUITER_PERMISSION_ASSIGNMENT],
  );

  const accountConfig = useStoreState((s) => s.app.accounts?.configuration);
  const isHireFormTemplateEnabled = !!accountConfig?.hireFormTemplateEnabled;
  const isCertnEnabled = !!accountConfig?.certnEnabled;
  const isI9CheckEnabled = !!accountConfig?.i9CheckEnabled;
  const isWorkBrightEnabled = !!accountConfig?.workbright?.enabled;

  // An object that maps a permission to the account config flag that enables it.
  const accountPermissions = {
    [EmployeePermissionsEnum.BACKGROUND_CHECK_ADMIN?.split('__')[0]]: isCertnEnabled,
    [EmployeePermissionsEnum.HR_ADMIN?.split('__')[0]]: isHireFormTemplateEnabled,
    [EmployeePermissionsEnum.I9_ADMIN?.split('__')[0]]: isI9CheckEnabled && isWorkBrightEnabled,
  };

  const defaultPermission = options?.permissions;
  const checkType = options?.checkType;

  /**
   * Checks if the user has the required permissions.
   *
   * @param params - An object with the following properties:
   *   - permissions: An array of EmployeePermissionsEnum values to check against.
   *   - checkType: How to check the permissions. If "all", then the user must have all the permissions.
   *     If not specified, then the user must have at least one of the permissions.
   * @returns A boolean indicating whether the user has the required permissions.
   */
  function checkPermission(params?: { permissions?: EmployeePermissionsEnum[]; checkType?: PermissionCheckType }): boolean {
    const permissionsToCheck = params?.permissions ?? defaultPermission;
    const permissionCheckType = params?.checkType ?? checkType;

    // If no permissions are provided and no defaultPermission is set, throw an error.
    if (!permissionsToCheck || !permissionsToCheck.length) {
      throw new Error('No permissions provided to check and no defaultPermission set.');
    }

    // Determine if the account configuration flags are enabled for the required permissions.
    const hasRequiredAccountFlags =
      permissionCheckType === 'all'
        ? permissionsToCheck.every((p) => accountPermissions[p.split('__')[0]])
        : permissionsToCheck.some((p) => accountPermissions[p.split('__')[0]]);

    if (!isRecruiterPermissionRemoteConfigEnabled) return hasRequiredAccountFlags;

    // Determine if the user has the required permissions based on the checkType.
    const hasRequiredPermissions =
      permissionCheckType === 'all'
        ? permissionsToCheck.every((p) => userPermissions.includes(p))
        : permissionsToCheck.some((p) => userPermissions.includes(p));

    // Return true if all conditions are met: remote config is enabled, user has required permissions,
    // and account flags are correctly set.
    return isRecruiterPermissionRemoteConfigEnabled && hasRequiredPermissions && hasRequiredAccountFlags;
  }

  return {
    checkPermission,
    permissions: userPermissions,
  };
}
